import React, { useEffect, useMemo, useState, useRef, useTransition } from 'react';
import './Operators.css';
import { useCache } from '../../hooks/useCache';
import { cacheGet, cacheSet, emitCacheUpdated } from '../../utils/cache';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend,
  PieChart, Pie, Cell
} from 'recharts';

// ==== Caches persistés pour la page Opérateurs ====
function safeLS(k){ try { return localStorage.getItem(k); } catch { return null; } }
const keyLogs = (uid) => `ops/logsCache/${uid || 'anon'}`;
const keyInc  = (uid) => `ops/incLogsCache/${uid || 'anon'}`;

const mapFromObj = (obj) => {
  const m = new Map();
  if (obj && typeof obj === 'object') for (const [k,v] of Object.entries(obj)) m.set(k, v);
  return m;
};
const persistMap = (map, key) => {
  const obj = {};
  map.forEach((v, k) => { obj[k] = v; });
  cacheSet(key, obj);
};

/* ========= Config API (sans toucher à src/config/api.js) ========= */
const API_BASE =
  (window?.ENV_API_BASE_URL) ||
  process.env.REACT_APP_API_BASE_URL ||
  "https://stericab-backend.onrender.com";

/* ========= Helpers Auth ========= */
function getAuthToken() {
  const keys = ["token","auth_token","jwt","jwt_token","access_token","accessToken"];
  for (const k of keys) {
    try {
      const v = localStorage.getItem(k);
      if (v && String(v).trim()) return String(v).trim();
    } catch {}
  }
  return null;
}
const authHeaders = () => {
  const t = getAuthToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/* ========= Helpers Dates & Grouping ========= */
const at00 = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };

// --- Jours FR (court & long)
const DOW_SHORT = ['Dim','Lun','Mar','Mer','Jeu','Ven','Sam'];
const DOW_LONG  = ['Dimanche','Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi'];

// ⬇️ à mettre avec tes helpers dates
const fromYMDLocal = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s||''));
  return m ? new Date(+m[1], +m[2]-1, +m[3]) : new Date(s);
};

// ⬇️ rends ddmm robuste si on lui passe un "YYYY-MM-DD"
const ddmm = (d) => {
  const x = (d instanceof Date)
    ? d
    : (/^\d{4}-\d{2}-\d{2}$/.test(String(d)) ? fromYMDLocal(d) : new Date(d));
  return `${String(x.getDate()).padStart(2,'0')}/${String(x.getMonth()+1).padStart(2,'0')}`;
};

// Lundi de la semaine d'une date donnée
function mondayOf(d0) {
  const d = new Date(d0);
  const day = d.getDay() || 7; // dim=0 -> 7
  if (day !== 1) d.setDate(d.getDate() - (day - 1));
  d.setHours(0,0,0,0);
  return d;
}

// Labels pour l'axe/tooltip
const dayAxisLabel   = (iso) => DOW_SHORT[fromYMDLocal(iso).getDay()];         // Lun/Mar/…
const dayTooltipText = (iso) => `${DOW_LONG[fromYMDLocal(iso).getDay()]} ${ddmm(iso)}`;
const weekAxisLabel  = (mondayIso) => ddmm(mondayIso);                      // 26/05
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const ymd = (d) => {
  const x = new Date(d);
  const Y = x.getFullYear();
  const M = String(x.getMonth()+1).padStart(2,'0');
  const D = String(x.getDate()).padStart(2,'0');
  return `${Y}-${M}-${D}`;
};
function isoWeek(d0) {
  const d = new Date(Date.UTC(d0.getFullYear(), d0.getMonth(), d0.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(),0,1));
  const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return { year: d.getUTCFullYear(), week: weekNo };
}
const weekKey = (d) => {
  const {year, week} = isoWeek(d);
  return `${year}-W${String(week).padStart(2,'0')}`;
};
const monthKey = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth()+1).padStart(2,'0')}`;
};
const dayKey = (d) => ymd(d);

const dayLabel = (k) => {
  const [Y,M,D] = k.split('-').map(Number);
  const x = new Date(Y, M-1, D);
  return `${String(x.getDate()).padStart(2,'0')}/${String(x.getMonth()+1).padStart(2,'0')}`;
};
const weekLabel = (k) => k.replace(/^\d{4}-/, ''); // "W05"
const monthLabel = (k) => {
  const [Y,M] = k.split('-');
  const months = ['Jan','Fév','Mar','Avr','Mai','Jui','Jui','Aoû','Sep','Oct','Nov','Déc'];
  return `${months[Number(M)-1]} ${Y.slice(2)}`;
};

function buildRange(period) {
  const today = at00(new Date());
  if (period === 'day') {
    const from = addDays(today, -6);
    return { from, to: today, fromYMD: ymd(from), toYMD: ymd(today) };
  }
  if (period === 'month') {
    const from = addDays(today, -30*5);
    return { from, to: today, fromYMD: ymd(from), toYMD: ymd(today) };
  }
  // default: week (12 semaines)
  const from = addDays(today, -7*11);
  return { from, to: today, fromYMD: ymd(from), toYMD: ymd(today) };
}

function makeBuckets(period, from, to) {
  const buckets = [];

  if (period === 'day') {
    for (let d = new Date(from); d <= to; d = addDays(d, 1)) {
      const key = dayKey(d); // YYYY-MM-DD
      buckets.push({ key, label: dayAxisLabel(key), tooltipKey: key });
    }
    return buckets;
  }

  if (period === 'month') {
    const cursor = new Date(from.getFullYear(), from.getMonth(), 1);
    const end = new Date(to.getFullYear(), to.getMonth(), 1);
    while (cursor <= end) {
      const key = monthKey(cursor);
      buckets.push({ key, label: monthLabel(key) });
      cursor.setMonth(cursor.getMonth()+1);
    }
    return buckets;
  }

  // period === 'week'
  const seen = new Set();
  for (let d = new Date(from); d <= to; d = addDays(d, 1)) {
    const key = weekKey(d); // YYYY-Www
    if (seen.has(key)) continue;
    seen.add(key);
    const mon = mondayOf(d);
    const monIso = ymd(mon);
    buckets.push({ key, label: weekAxisLabel(monIso), mondayIso: monIso, tooltipKey: monIso });
  }
  return buckets;
}

/* ========= Mapping incidents ========= */
function isDone(log){ return statusNormalized(log) === 'done'; }
function isLate(log) {
  const t = String(log.action_type || '').toLowerCase();
  const s = statusOf(log);
  const meta = log.meta || {};
  return t === 'late' || s === 'late' || meta.late === true;
}
function isNotDone(log) {
  const t = String(log.action_type || '').toLowerCase();
  const s = statusOf(log);
  return t === 'failed' || t === 'not_done' || t === 'notdone' || s === 'failed' || s === 'not_done';
}
function isNotFeasible(log) {
  const t = String(log.action_type || '').toLowerCase();
  const s = statusOf(log);
  return t === 'not_feasible' || t === 'cancelled' || t === 'canceled' || 
         s === 'not_feasible' || s === 'cancelled' || s === 'canceled';
}
function isIncident(log){ return ['incident','failed','missed'].includes(statusNormalized(log)); }

/* ========= Utilitaires robustes ========= */
// ── Récupère un tableau quel que soit le shape renvoyé par l'API
function asArray(x) {
  if (Array.isArray(x)) return x;
  if (x && typeof x === 'object') {
    for (const k of ['logs','items','data','results','rows']) {
      if (Array.isArray(x[k])) return x[k];
    }
  }
  return [];
}

// ── Clef opérateur STRING (supporte number, string, uuid)
function assignedKeyOf(l) {
  const cand = [
    l?.meta?.assigned_id, l?.meta?.operator_id, l?.meta?.assignee_id,
    l?.operator_id, l?.assigned_id, l?.assignee_id,
    l?.user_id, l?.meta?.user_id, l?.meta?.owner_id
  ];
  const v = cand.find(v => v !== undefined && v !== null && String(v).trim() !== '');
  return v === undefined || v === null ? '' : String(v).trim();
}

function sameOp(a, b) {
  if (!a || !b) return false;
  return String(a) === String(b) || Number(a) === Number(b);
}

/* ========= Tasks from logs.meta (due_date + assigned_id) ========= */
const toNum = v => Number(String(v ?? '').replace(/[^\d-]/g,'')) || 0;

const statusOf = (l) => String(l?.meta?.status ?? l?.status ?? '').toLowerCase();

/** Renvoie l'un de: 'done' | 'incident' | 'failed' | 'missed' | null */
function statusNormalized(l) {
  // 1) booléens explicites
  if (l?.meta?.completed === true || l?.completed === true) return 'done';
  if (l?.meta?.incident === true || l?.incident === true)   return 'incident';
  if (l?.meta?.failed === true   || l?.failed === true)     return 'failed';
  if (l?.meta?.missed === true   || l?.missed === true)     return 'missed';
  // 2) strings usuelles
  const candidates = [
    String(l?.meta?.status ?? '').toLowerCase(),
    String(l?.status ?? '').toLowerCase(),
    String(l?.action_type ?? '').toLowerCase(),
    String(l?.meta?.event ?? '').toLowerCase(),
    String(l?.meta?.action ?? '').toLowerCase(),
  ].filter(Boolean);
  for (const s of candidates) {
    if (s === 'done') return 'done';
    if (s === 'incident') return 'incident';
    if (s === 'failed' || s === 'fail' || s === 'error') return 'failed';
    if (s === 'missed' || s === 'miss' || /miss(ed)?/.test(s)) return 'missed';
  }
  return null;
}
const isTaskLike = (l) => {
  const m = l?.meta || {};
  return !!(
    m.task_instance_id || l?.task_instance_id ||
    m.task_id || l?.task_id ||
    m.task_title || l?.title ||
    m.checklist ||
    m.assigned_id || l?.assigned_id ||
    m.operator_id || l?.operator_id ||
    m.assignee_id || l?.assignee_id
  );
};
const assignedIdOf = (l) => toNum(
  l?.meta?.assigned_id ??
  l?.meta?.operator_id ??
  l?.meta?.assignee_id ??
  l?.operator_id ??
  l?.user_id ??
  l?.meta?.user_id ??
  l?.meta?.owner_id
);

// Liste des opérateurs concernés par un log :
// 1) meta.assigned_id prioritaire
// 2) meta.assigned = "tout le monde" / "all" / "everyone" / "tous" => tous les opérateurs
// 3) fallback: "prenom nom" qui matche un opérateur
function assignedIdsFor(log, operators) {
  const m = log?.meta || {};
  const id = assignedIdOf(log);
  if (id) return [String(id)];
  const raw = String(m.assigned || m.assignee || '').trim().toLowerCase();
  if (!raw) return [];
  const ALL = ['tout le monde','all','everyone','tous','global','globale'];
  if (ALL.includes(raw)) return (operators || []).map(o => String(o.id));
  const norm = raw.replace(/\s+/g,' ');
  const found = (operators || []).find(o =>
    (`${String(o.first_name||'').trim()} ${String(o.last_name||'').trim()}`.toLowerCase() === norm)
  );
  return found ? [String(found.id)] : [];
}

function emptyPieCounters() {
  return { done:0, incident:0, notDone:0, notFeasible:0 };
}

// ID robuste pour une tâche (sert pour les cases cochées)
function taskId(t){
  return String(
    t?.id ?? t?.task_id ?? t?.meta?.task_id ?? t?.meta?.task_instance_id ?? t?.uuid ?? `${t?.title || 'task'}|${t?.created_at || Math.random()}`
  );
}

// ── Normalisation
const asId = v => {
  if (v === undefined || v === null) return null;
  const s = String(v).trim();
  return (s === '' || s.toLowerCase() === 'null') ? null : s;
};

// ── assigned_id STRICT (on garde quelques alias en secours ; supprime-les si tu veux 100% 'assigned_id')
function getAssignedIdStrict(t) {
  return asId(
    t.assigned_id ??
    t?.meta?.assigned_id ??
    t.assignee_id ??            // alias tolérés
    t.operator_id ??            // (facile à retirer)
    t?.meta?.operator_id ??
    null
  );
}

// ── weekday (1..7) depuis l'objet tâche
function getTaskWeekday(t, fallbackWeekday = null) {
  const n = Number(t.weekday ?? t?.meta?.weekday);
  if (Number.isInteger(n) && n >= 1 && n <= 7) return n;

  const ymd =
    t.date || t.scheduled_for || t.due_date || t?.meta?.date || null;
  if (typeof ymd === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    // 'YYYY-MM-DD' en JS est interprété en UTC → le jour de semaine reste correct
    const d = new Date(ymd + 'T00:00:00Z');
    const dow0 = d.getUTCDay();         // 0=dim..6=sam
    return dow0 === 0 ? 7 : dow0;       // 1=lun..7=dim
  }
  return fallbackWeekday;                // optionnel: jour du contexte (vue/jour sélectionné)
}

// ── L'opérateur travaille-t-il ce weekday ? (vue "flat" renvoyée par l'API)
function isWorkingOn(weeklyFlat, weekday) {
  if (!weeklyFlat) return false;
  const rec = weeklyFlat[String(weekday)];
  return !!rec; // non-null ⇒ jour configuré et actif dans la période effective
}

function workingDaysFromWeekly(weeklyFlat) {
  // weeklyFlat attendu sous la forme { "1": {...} | null, ..., "7": null }
  const set = new Set();
  if (!weeklyFlat || typeof weeklyFlat !== 'object') return set;
  for (const k of ['1','2','3','4','5','6','7']) {
    if (weeklyFlat[k]) set.add(Number(k)); // jour actif si non-null
  }
  return set; // ex: Set {1,2,3,4,5}
}

// Base (sans préférence) : la tâche s'applique-t-elle à l'opérateur ?
function baseAppliesToOperator(task, operatorId, weeklyFlat) {
  const opId = String(operatorId);
  const assigned = getAssignedIdStrict(task); // null = "tout le monde"

  // Assignée à cet opérateur ?
  if (assigned !== null && String(assigned) === opId) return true;

  // Assignée à un autre opérateur ?
  if (assigned !== null && String(assigned) !== opId) return false;

  // "Tout le monde" → vérifier le jour travaillé si on peut le déterminer
  const workingDays = workingDaysFromWeekly(weeklyFlat);
  const wd = getTaskWeekday(task);
  if (!wd || workingDays.size === 0) return true; // pas d'info → on garde
  return workingDays.has(wd);
}

function belongsToUser(t, uid) {
  const U = String(uid || '').trim();
  if (!U) return false; // si pas d'uid, on ne montre rien
  const cands = [
    t?.user_id, t?.owner_id,
    t?.meta?.user_id, t?.meta?.owner_id,
    t?.account_id, t?.meta?.account_id
  ].map(v => (v === undefined || v === null) ? '' : String(v));
  return cands.includes(U);
}

// Transforme une tâche brute en ligne de tableau (simplifié)
function taskToRowSimple(t){
  return { id: taskId(t), title: t.title || t.name || 'Tâche', __raw: t };
}

function parseSafeDate(raw) {
  if (!raw) return null;
  const s = String(raw).trim();

  // "YYYY-MM-DD"  -> date locale (évite les décalages UTC)
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [Y, M, D] = s.split('-').map(Number);
    return new Date(Y, M - 1, D);
  }

  // "YYYY-MM-DD HH:MM(:SS)?" -> interpréter comme LOCAL (PG/Neon affiche souvent ce format)
  if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?$/.test(s)) {
    const norm = s.replace(' ', 'T');
    return new Date(norm);
  }

  const d = new Date(s);
  return isNaN(d) ? null : d;
}

function eventDateForLog(l) {
  // 1) created_at (toujours prioritaire)
  const prefer = l?.created_at ?? l?.createdAt ?? l?.meta?.created_at ?? l?.meta?.timestamp ?? l?.timestamp;
  const d0 = parseSafeDate(prefer);
  if (d0) return d0;

  // 2) fallback exécution réelle
  const exec = [
    l?.performed_at, l?.completed_at, l?.done_at, l?.closed_at,
    l?.meta?.performed_at, l?.meta?.completed_at, l?.meta?.done_at, l?.meta?.closed_at
  ];
  for (const c of exec) { const d = parseSafeDate(c); if (d) return d; }

  // 3) fallback dates planifiées
  const plan = [ l?.meta?.occur_date, l?.meta?.due_date, l?.date, l?.meta?.date ];
  for (const c of plan) { const d = parseSafeDate(c); if (d) return d; }

  // 4) dernier recours
  return new Date();
}

function bucketKeyForTask(l, period) {
  const d = eventDateForLog(l);
  if (period === 'day')   return ymd(d);
  if (period === 'month') return monthKey(d);
  return weekKey(d);
}
function dedupeByInstance(logs) {
  const seen = new Set();
  const out = [];
  for (const l of (Array.isArray(logs) ? logs : [])) {
    const m = l?.meta || {};
    // clé de repli si pas de task_instance_id
    const fallbackKey = [
      m.task_title || l.title || '',
      m.assigned_id || m.operator_id || l.operator_id || '',
      (m.due_date || l.created_at || l.createdAt || '').toString().slice(0,10)
    ].join('|');

    const key = m.task_instance_id || fallbackKey;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(l);
  }
  return out;
}

/* ========= API calls (GET only) ========= */
async function apiGet(path, params = {}) {
  const { signal, ...query } = params;
  const url = new URL(API_BASE.replace(/\/+$/,'') + path);
  Object.entries(query).forEach(([k,v]) => {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, v);
  });
  const res = await fetch(url.toString(), {
    signal,
    headers: { 'Content-Type': 'application/json', ...authHeaders() }
  });
  if (!res.ok) {
    const txt = await res.text().catch(()=> '');
    const err = new Error(`GET ${path} -> ${res.status} ${txt}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

async function apiPost(path, body = {}) {
  const res = await fetch(API_BASE.replace(/\/+$/,'') + path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(body)
  });
  if (!res.ok) {
    let detail = await res.text().catch(()=> '');
    try { const j = JSON.parse(detail); detail = j.error || j.message || detail; } catch {}
    const err = new Error(`POST ${path} -> ${res.status} ${detail}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

async function apiPut(path, body = {}) {
  const res = await fetch(API_BASE.replace(/\/+$/,'') + path, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(body)
  });
  if (!res.ok) {
    const txt = await res.text().catch(()=> '');
    const err = new Error(`PUT ${path} -> ${res.status} ${txt}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

async function apiDelete(path) {
  const res = await fetch(API_BASE.replace(/\/+$/,'') + path, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...authHeaders() }
  });
  if (!res.ok) {
    const txt = await res.text().catch(()=> '');
    const err = new Error(`DELETE ${path} -> ${res.status} ${txt}`);
    err.status = res.status;
    throw err;
  }
  return res.json().catch(()=> ({}));
}

// === Operator Task Prefs API ===
async function apiGetTaskPrefs(operatorId) {
  return apiGet(`/api/operators/${operatorId}/task-prefs`);
}
async function apiUpsertTaskPref(operatorId, taskId, mode, note = null) {
  return apiPost(`/api/operators/${operatorId}/task-prefs`, { task_id: Number(taskId), mode, note });
}
async function apiDeleteTaskPref(operatorId, taskId) {
  return apiDelete(`/api/operators/${operatorId}/task-prefs/${Number(taskId)}`);
}

// clés cache
const OPS_LIST_KEY       = 'ops/list';
const OP_INDEX_KEY       = 'operators/index'; // déjà utilisé ailleurs
const OPS_WH_KEY         = 'ops/weeklyHours';
const OPS_PREFS_KEY      = 'ops/taskPrefs';
const OPS_KPI_BY_OP_KEY  = 'ops/kpiByOp';
const OPS_SERIES7W_KEY   = 'ops/series7w';
const OPS_SERIES7M_KEY   = 'ops/series7m';

/* ========= Page Operators ========= */
export default function Operators({ onNavigate }) {
  // 1) Lecture instantanée depuis le bootstrap
  const operatorsCached = useCache('operators/list') ?? [];
  const weeklyById      = useCache('operators/weekly-hours/by-id') ?? {};
  const aggDay          = useCache('operators/agg/day')   ?? null;
  const aggWeek         = useCache('operators/agg/week')  ?? null;
  const aggMonth        = useCache('operators/agg/month') ?? null;
  const incDay          = useCache('operators/incidents/day')   ?? { statsByOp:{}, lastByOp:{} };
  const incWeek         = useCache('operators/incidents/week')  ?? { statsByOp:{}, lastByOp:{} };
  const incMonth        = useCache('operators/incidents/month') ?? { statsByOp:{}, lastByOp:{} };
  const tasksDayAgg   = useCache('tasks/operators/day')   ?? null;
  const tasksWeekAgg  = useCache('tasks/operators/week')  ?? null;
  const tasksMonthAgg = useCache('tasks/operators/month') ?? null;
  const tasksListCached = useCache('tasks/list') ?? [];

  // uid dynamique (liée à la session active)
  const uid = useMemo(() => safeLS('userId') || safeLS('uid') || '', []);

  // caches en mémoire, liés à l'uid courant
  const logsCacheRef = useRef(mapFromObj(cacheGet(keyLogs(uid), {})));
  const incCacheRef  = useRef(mapFromObj(cacheGet(keyInc(uid), {})));
  const logsCache = logsCacheRef.current;
  const incCache  = incCacheRef.current;

  // borne simple: garder les N dernières fenêtres
  const trimMapInPlace = (m, max = 12) => { while (m.size > max) m.delete(m.keys().next().value); };

  // état initial : ZERO NETWORK, lecture instantanée
  const [operators, setOperators]       = useState(() => cacheGet(OPS_LIST_KEY, []));
  const [opIndex, setOpIndex]           = useState(() => cacheGet(OP_INDEX_KEY, {}));
  const [weeklyHours, setWeeklyHours]   = useState(() => cacheGet(OPS_WH_KEY, {}));
  const [taskPrefs, setTaskPrefs]       = useState(new Map());
  const [kpiByOp, setKpiByOp]           = useState(() => cacheGet(OPS_KPI_BY_OP_KEY, { jour:{}, semaine:{}, mois:{} }));
  const [series7w, setSeries7w]         = useState(() => cacheGet(OPS_SERIES7W_KEY, []));
  const [series7m, setSeries7m]         = useState(() => cacheGet(OPS_SERIES7M_KEY, []));

  // 👉 DOIT être déclaré avant tout effet/const qui l'utilise
  const [selectedId, setSelectedId] = useState(
    () => (Array.isArray(operatorsCached) && operatorsCached.length ? operatorsCached[0].id : null)
  );

  // Ref pour stabiliser le listener (éviter les re-créations)
  const selectedIdRef = useRef(selectedId);
  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);

  const operatorsSafe = useMemo(
    () => (Array.isArray(operators) ? operators : []),
    [operators]
  );

  // écoute des refresh bootstrap (focus / revalidation silencieuse)
  useEffect(() => {
    const onUpd = (e) => {
      const t = e?.detail?.topic;
      if (!t) return;
      // Conserver la logique existante, mais déclenchée sur 'bootstrap'
      if (t === 'bootstrap') {
        setOperators(cacheGet(OPS_LIST_KEY, []));
        setOpIndex(cacheGet(OP_INDEX_KEY, {}));
        setWeeklyHours(cacheGet(OPS_WH_KEY, {}));
        setKpiByOp(cacheGet(OPS_KPI_BY_OP_KEY, { jour:{}, semaine:{}, mois:{} }));
        setSeries7w(cacheGet(OPS_SERIES7W_KEY, []));
        setSeries7m(cacheGet(OPS_SERIES7M_KEY, []));
        
        // Pour les prefs, utiliser la ref pour éviter les dépendances
        const allPrefs = cacheGet(OPS_PREFS_KEY, {});
        const sid = String(asId(selectedIdRef.current) ?? '');
        const opPrefs = sid && allPrefs[sid] ? allPrefs[sid] : {};
        setTaskPrefs(new Map(Object.entries(opPrefs)));
      }
    };
    window.addEventListener('cc:cache-updated', onUpd);
    return () => window.removeEventListener('cc:cache-updated', onUpd);
  }, []); // IMPORTANT: listener créé une seule fois

  // Gestion du changement d'utilisateur (si jamais la page permet un changement sans reload)
  useEffect(() => {
    const handler = (e) => {
      if (e?.detail?.topic !== 'bootstrap') return;
      const now = safeLS('userId') || safeLS('uid') || '';
      if (now && now !== uid) {
        logsCache.clear();
        incCache.clear();
        // (optionnel) nettoie aussi le persistant de l'ancien uid pour éviter le bloat
        // cacheSet(keyLogs(uid), {}); cacheSet(keyInc(uid), {});
      }
    };
    window.addEventListener('cc:cache-updated', handler);
    return () => window.removeEventListener('cc:cache-updated', handler);
  }, [uid]);

  const [mode, setMode] = useState('agenda'); // 'agenda' | 'absence' | 'documents'

  const [period, setPeriod] = useState('week'); // 'day' | 'week' | 'month'
  const range = useMemo(() => buildRange(period), [period]);

  const [incPeriod, setIncPeriod] = useState('week'); // 'week' | 'month' | '6m'
  const [incPage, setIncPage] = useState(1);
  const INC_PAGE_SIZE = 10;
  const [incidentItems, setIncidentItems] = useState([]); // [{date,title,status}]

  const incRange = useMemo(() => {
    const today = at00(new Date()); // 00:00 local
    if (incPeriod === 'week') {                 // ⇐ 7 jours glissants
      const from = addDays(today, -6);
      return { from, to: today, fromYMD: ymd(from), toYMD: ymd(today) };
    }
    if (incPeriod === 'month') {                // ⇐ 30 jours glissants
      const from = addDays(today, -29);
      return { from, to: today, fromYMD: ymd(from), toYMD: ymd(today) };
    }
    // ⇐ 6 mois ~ 180 jours glissants
    const from = addDays(today, -179);
    return { from, to: today, fromYMD: ymd(from), toYMD: ymd(today) };
  }, [incPeriod]);

  useEffect(() => { setIncPage(1); }, [incPeriod, selectedId]);

  // Recadrer la pagination si le nombre d'items change
  useEffect(() => {
    const pages = Math.max(1, Math.ceil(incidentItems.length / INC_PAGE_SIZE));
    if (incPage > pages) setIncPage(pages);
  }, [incidentItems.length, incPage]);

  // const [loading, setLoading] = useState(true);
  const toExclInit = ymd(addDays(range.to, 1));
  const chartHas   = logsCache.has(`${range.fromYMD}|${toExclInit}`);
  const incToExcl  = ymd(addDays(incRange.to, 1));
  const incHas     = incCache.has(`${incRange.fromYMD}|${incToExcl}`);
  const sidStr = String(selectedId ?? '');
  const hasWeekly  = sidStr && (weeklyById?.[sidStr] || weeklyHours?.[sidStr]);
  const [loadingCards, setLoadingCards] = useState({
    hours: !hasWeekly,
    chart: !chartHas,
    pie:   !incHas,
    history: !incHas,
    table: !(Array.isArray(tasksListCached) && tasksListCached.length)
  });
  const [error, setError] = useState('');

  // Cards data
  const [weekHours, setWeekHours] = useState([]); // [{label, active, start, end, endNextDay}]
  const [seriesChart, setSeriesChart] = useState([]); // [{label, op, avg}]
  const [pieStats, setPieStats] = useState({ done:0, late:0, notDone:0, notFeasible:0 });
  // uid déjà déclaré plus haut avec safeLS
  const bootstrapTasks = useMemo(
    () => (Array.isArray(tasksListCached) ? tasksListCached.filter(t => belongsToUser(t, uid)) : []),
    [tasksListCached, uid]
  );
  const [tasksAll, setTasksAll] = useState(bootstrapTasks); // ✅ affichage immédiat
  const [checkedTaskIds, setCheckedTaskIds] = useState(new Set()); // cases cochées
  
  // état local (mémo par opérateur)
  const [weeklyHoursByOp, setWeeklyHoursByOp] = useState({}); // { [operatorId]: { week, tz, fetchedAt } }
  
  // Prefs (operator_task_prefs) : Map<taskIdString, 'exclude'|'include'>
  const [prefsLoading, setPrefsLoading] = useState(false);

  // Ticks d'invalidation pour forcer les recomputes
  const [chartCacheTick, setChartCacheTick] = useState(0);
  const [incCacheTick, setIncCacheTick] = useState(0);
  const [isPending, startTransition] = useTransition();

  const cacheKey = (fromYMD, toYMDExcl) => `${fromYMD}|${toYMDExcl}`;

  // Simplifier taskRows (pas d'état, juste un memo)
  const taskRows = useMemo(
    () => (tasksAll || []).map(taskToRowSimple),
    [tasksAll]
  );

  // Auto-cochage : superposer les prefs au calcul existant
  useEffect(() => {
    const sid = asId(selectedId);
    if (!sid) { setCheckedTaskIds(new Set()); return; }

    startTransition(() => {
      const weeklyFlat = weeklyHoursByOp[sid]?.week || weeklyById?.[sid] || weeklyHours?.[sid] || null;
      const next = new Set();

      for (const t of tasksAll) {
        const idStr = taskId(t);

        // 1) Préférence explicite ?
        const pref = taskPrefs?.get?.(String(t.id ?? t.task_id ?? idStr));
        if (pref === 'include') { next.add(idStr); continue; }
        if (pref === 'exclude') { /* forcée décochée */ continue; }

        // 2) Sinon: base (sans préférences)
        if (baseAppliesToOperator(t, sid, weeklyFlat)) next.add(idStr);
      }

      setCheckedTaskIds(next);
    });
  }, [selectedId, tasksAll, weeklyHoursByOp, weeklyById, weeklyHours, taskPrefs]);

  // Debug temporaire pour vérifier les assignations
  useEffect(() => {
    if (!tasksAll.length) return;
    const sid = asId(selectedId);
    const weeklyFlat = weeklyHoursByOp[sid]?.week || weeklyById?.[sid] || weeklyHours?.[sid] || null;
    const workingDays = workingDaysFromWeekly(weeklyFlat);
    const sample = tasksAll.slice(0,5).map(t => {
      const a = getAssignedIdStrict(t);
      const wd = getTaskWeekday(t);
      const pref = taskPrefs?.get?.(String(t.id ?? t.task_id ?? taskId(t)));
      
      let shouldBeChecked = false;
      let reason = '';
      
      if (pref === 'include') {
        shouldBeChecked = true;
        reason = 'forcée include';
      } else if (pref === 'exclude') {
        shouldBeChecked = false;
        reason = 'forcée exclude';
      } else {
        // Base (sans préférence)
        if (a === null) {
          const worksThatDay = !wd || workingDays.size === 0 || workingDays.has(wd);
          shouldBeChecked = worksThatDay;
          reason = worksThatDay ? 'tout le monde (jour travaillé)' : 'tout le monde (jour non travaillé)';
        } else if (String(a) === String(sid) || Number(a) === Number(sid)) {
          shouldBeChecked = true;
          reason = 'assignée à cet op';
        } else {
          shouldBeChecked = false;
          reason = 'assignée à un autre';
        }
      }
      
      return { 
        title: t.title || t.name, 
        assignedId: a, 
        weekday: wd, 
        workingDays: Array.from(workingDays),
        pref,
        shouldBeChecked,
        reason
      };
    });
    console.debug('[Tasks auto-check WITH PREFS]', { selectedId: sid, sample });
  }, [tasksAll, selectedId, weeklyHoursByOp, weeklyById, weeklyHours, taskPrefs]);


  
  // récupère la vue "flat" des jours travaillés
  async function loadWeeklyHours(opId, token) {
    if (!opId) return;
    try {
      const res = await fetch(`${API_BASE}/api/operators/${opId}/weekly-hours`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('weekly-hours fetch failed');
      const week = await res.json(); // { "1": {...} | null, ..., "7": null }
      const tz = Object.values(week).find(Boolean)?.tz || 'Europe/Paris';
      setWeeklyHoursByOp(prev => ({ ...prev, [opId]: { week, tz, fetchedAt: Date.now() } }));
    } catch (e) {
      console.warn('[weekly-hours]', e);
      setWeeklyHoursByOp(prev => ({ ...prev, [opId]: { week: null, tz: 'Europe/Paris', fetchedAt: Date.now() } }));
    }
  }

  // Mise à jour optimiste des horaires hebdomadaires
  function upsertLocalWeek(opId, weekday, payloadOrNull) {
    setWeeklyHours(prev => {
      const next = { ...prev, [opId]: { ...(prev[opId]||{}) } };
      next[opId][String(weekday)] = payloadOrNull; // ou null pour désactiver
      cacheSet(OPS_WH_KEY, next);
      emitCacheUpdated(OPS_WH_KEY);
      return next;
    });
    // fire-and-forget: fetch(...) vers /weekly-hours ou /bulk
  }

  // (re)charger quand l'opérateur change
  useEffect(() => {
    const sid = asId(selectedId);
    if (!sid) return;
    if (!weeklyHoursByOp[sid]) loadWeeklyHours(sid, getAuthToken());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // Charger les préférences de l'opérateur sélectionné (depuis le cache)
  useEffect(() => {
    const sid = asId(selectedId);
    if (!sid) { 
      setTaskPrefs(new Map()); 
      return; 
    }

    // Lecture depuis le cache (instantané)
    const allPrefs = cacheGet(OPS_PREFS_KEY, {});
    const opPrefs = allPrefs[sid] || {};
    const m = new Map();
    Object.entries(opPrefs).forEach(([taskId, mode]) => m.set(taskId, mode));
    setTaskPrefs(m);
  }, [selectedId]);

  // === Charger les prefs depuis l'API quand l'opérateur change ===
  useEffect(() => {
    const sid = asId(selectedId);
    if (!sid) { setTaskPrefs(new Map()); return; }

    // 1) Instantané : depuis le cache (si présent)
    const cachedAll = cacheGet(OPS_PREFS_KEY, {}) || {};
    const cached = cachedAll[String(sid)];
    if (cached) {
      setTaskPrefs(new Map(Object.entries(cached)));
      setPrefsLoading(false); // ✅ ne bloque rien
    }

    let alive = true;

    (async () => {
      try {
        // 2) Revalidation silencieuse uniquement si absent OU si tu veux forcer un refresh périodique
        if (cached) { /* fetch en arrière-plan non-bloquant */ }
        else { setPrefsLoading(true); }
        
        const res = await apiGetTaskPrefs(sid);
        // tolère plusieurs shapes: {prefs:[...]}, [...] ou objet générique
        const arr = Array.isArray(res?.prefs) ? res.prefs : (Array.isArray(res) ? res : asArray(res));

        const mapping = {};
        for (const p of arr) {
          const tid  = String(p.task_id ?? p.taskId ?? p?.task?.id ?? p.id ?? '').trim();
          const mode = String(p.mode ?? p.preference ?? '').toLowerCase();
          if (tid && (mode === 'exclude' || mode === 'include')) mapping[tid] = mode;
        }

        if (!alive) return;
        // alimente l'état runtime
        setTaskPrefs(new Map(Object.entries(mapping)));

        // alimente le cache partagé (pour les autres composants / prochains renders)
        const all = cacheGet(OPS_PREFS_KEY, {}) || {};
        all[String(sid)] = mapping;
        cacheSet(OPS_PREFS_KEY, all);
        emitCacheUpdated(OPS_PREFS_KEY);
      } catch (e) {
        console.warn('[task-prefs fetch]', e);
        // on laisse le cache local faire foi si dispo
      } finally {
        if (alive) setPrefsLoading(false);
      }
    })();

    return () => { alive = false; };
  }, [selectedId]);

  const toggleTaskChecked = async (rowId) => {
    const sid = asId(selectedId);
    if (!sid) return;

    // retrouve la tâche depuis tasksAll
    const t = (tasksAll || []).find(x => taskId(x) === rowId);
    if (!t) return;

    // état avant/après
    const wasChecked = checkedTaskIds.has(rowId);
    const willBeChecked = !wasChecked;

    // base (sans préférence) pour savoir si on doit créer/supprimer une pref
    const weeklyFlat = weeklyHoursByOp[sid]?.week || weeklyById?.[sid] || weeklyHours?.[sid] || null;
    const base = baseAppliesToOperator(t, sid, weeklyFlat);

    // Optimistic UI
    setCheckedTaskIds(prev => {
      const n = new Set(prev);
      if (willBeChecked) n.add(rowId); else n.delete(rowId);
      return n;
    });

    try {
      const key = String(t.id ?? t.task_id ?? '');
      const keyNum = Number(key);
      const hasNumericId = Number.isFinite(keyNum);
      const existing = taskPrefs?.get?.(key);

      if (willBeChecked === base) {
        // L'utilisateur remet le comportement par défaut → supprimer la préférence si elle existe
        if (existing && hasNumericId) {
          await apiDeleteTaskPref(sid, keyNum);
          // Mise à jour du cache local
          const allPrefs = cacheGet(OPS_PREFS_KEY, {});
          if (allPrefs[sid]) {
            delete allPrefs[sid][key];
            if (Object.keys(allPrefs[sid]).length === 0) delete allPrefs[sid];
            cacheSet(OPS_PREFS_KEY, allPrefs);
            emitCacheUpdated(OPS_PREFS_KEY);
          }
          setTaskPrefs(prev => {
            const m = new Map(prev);
            m.delete(key);
            return m;
          });
        }
      } else {
        // L'utilisateur force un état contraire au défaut
        const mode = willBeChecked ? 'include' : 'exclude';
        if (!hasNumericId) {
          console.warn('[prefs] tâche sans id numérique, pas d\'upsert', { rowId, key });
          return; // on n'écrit pas de pref sans ID côté back
        }
        await apiUpsertTaskPref(sid, keyNum, mode);
        // Mise à jour du cache local
        const allPrefs = cacheGet(OPS_PREFS_KEY, {});
        if (!allPrefs[sid]) allPrefs[sid] = {};
        allPrefs[sid][key] = mode;
        cacheSet(OPS_PREFS_KEY, allPrefs);
        emitCacheUpdated(OPS_PREFS_KEY);
        setTaskPrefs(prev => {
          const m = new Map(prev);
          m.set(key, mode);
          return m;
        });
      }
    } catch (e) {
      // rollback visuel si erreur
      setCheckedTaskIds(prev => {
        const n = new Set(prev);
        if (wasChecked) n.add(rowId); else n.delete(rowId);
        return n;
      });
      console.error('[task-prefs toggle]', e);
      alert('Impossible d\'enregistrer la préférence pour cette tâche.');
    }
  };

  // Opérateur sélectionné (objet)
  const selectedOp = useMemo(
    () => operatorsSafe.find(o => String(o.id) === String(selectedId)) || null,
    [operatorsSafe, selectedId]
  );

  // Utilise les agrégats bootstrap si dispo (instantané)
  function getAggForPeriod(p) {
    if (p === 'day')   return tasksDayAgg;
    if (p === 'month') return tasksMonthAgg;
    return tasksWeekAgg;
  }

  function getOpAggForPeriod(p) {
    if (p === 'day')   return aggDay;
    if (p === 'month') return aggMonth;
    return aggWeek;
  }
  function getIncForPeriod(p) {
    if (p === 'day')   return incDay;
    if (p === 'month') return incMonth;
    return incWeek;
  }

  function buildSeriesFromCache(period, selectedId, opsCount) {
    const agg = getAggForPeriod(period);
    if (!agg) return null;
    // On tolère 2 formes : { buckets:[], perOp:{[id]:{[bucket]:count}}, totals:{[bucket]:n} }
    // ou bien un tableau d'objets {operator_id, bucket, count} + totals séparés.
    try {
      if (agg.buckets && agg.perOp && agg.totals) {
        const divisor = Math.max(1, opsCount);
        return agg.buckets.map(b => ({
          key: b.key,                       // +++
          monday: b.mondayIso,              // +++ (si présent)
          label: b.label || b.key,
          op: (agg.perOp?.[selectedId]?.[b.key] ?? 0),
          avg: Number(((agg.totals[b.key] || agg.totals[b] || 0) / divisor).toFixed(2))
        }));
      }
      // fallback: tableau plat
      const rows = Array.isArray(agg) ? agg : [];
      const buckets = makeBuckets(period, range.from, range.to);
      const byOpBucket = {};
      const totals = {};
      for (const r of rows) {
        const bid = String(r.bucket || r.key || '');
        totals[bid] = (totals[bid] || 0) + (r.count|0);
        const oid = String(r.operator_id ?? r.op_id ?? '');
        byOpBucket[oid] = byOpBucket[oid] || {};
        byOpBucket[oid][bid] = (byOpBucket[oid][bid] || 0) + (r.count|0);
      }
      const divisor = Math.max(1, opsCount);
      return buckets.map(b => ({
        key: b.key,                       // +++
        monday: b.mondayIso,              // +++
        label: b.label,
        op: (byOpBucket?.[String(selectedId)]?.[b.key] ?? 0),
        avg: Number(((totals[b.key] || 0) / divisor).toFixed(2))
      }));
    } catch { return null; }
  }

  // Si le cache bootstrap arrive après le premier render, met à jour l'état
  useEffect(() => {
    setOperators(Array.isArray(operatorsCached) ? operatorsCached : []);
  }, [operatorsCached]);

  // Si rien de sélectionné, choisir le premier opérateur disponible
  useEffect(() => {
    if (!selectedId && operatorsSafe.length) setSelectedId(operatorsSafe[0].id);
  }, [operatorsSafe, selectedId]);

  // Optionnel : Fallback réseau si le cache est vide (rare)
  useEffect(() => {
    if (operatorsCached && operatorsCached.length) return;
    let alive = true;
    (async () => {
      try {
        const ops = await apiGet('/api/operators', uid ? { user_id: uid } : {});
        if (alive && Array.isArray(ops)) setOperators(ops);
      } catch (e) { /* silencieux, on a un cache */ }
    })();
    return () => { alive = false; };
  }, [uid, operatorsCached]);

  // ---- Tâches (toutes celles du compte user_id) ----
  useEffect(() => {
    const hasCached = (Array.isArray(tasksListCached) && tasksListCached.length) || (tasksAll && tasksAll.length);
    if (!hasCached) setLoadingCards(p => ({ ...p, table:true }));
    let alive = true;
    (async () => {
      try {
        const res = await apiGet('/api/tasks', uid ? { user_id: uid, limit: 2000 } : { limit: 2000 });
        const arr = Array.isArray(res?.tasks) ? res.tasks : (Array.isArray(res) ? res : asArray(res));
        const arrUid = (arr || []).filter(t => belongsToUser(t, uid));
        if (!alive) return;
        setTasksAll(arrUid);
      } catch {
        // fallback sur le cache si dispo
        const arr = Array.isArray(tasksListCached) ? tasksListCached : [];
        const arrUid = (arr || []).filter(t => belongsToUser(t, uid));
        setTasksAll(arrUid);
      } finally {
        if (alive) setLoadingCards(p => ({ ...p, table:false }));
      }
    })();
    return () => { alive = false; };
  }, [uid, operatorsSafe]); // si la liste d'opérateurs arrive après



  /* ---- Load weekly hours (effet dédié) ---- */
  useEffect(() => {
    const hasCached = selectedId && (weeklyById?.[selectedId] || weeklyHours?.[selectedId]);
    if (!hasCached) setLoadingCards(prev => ({ ...prev, hours: true }));
    let alive = true;
    const ac = new AbortController();

            // 0) Affichage instantané depuis le cache bootstrap
        if (sidStr && (weeklyById?.[sidStr] || weeklyHours?.[sidStr])) {
          setWeekHours(formatWeeklyHours(weeklyById?.[sidStr] || weeklyHours?.[sidStr]));
          setLoadingCards(prev => ({ ...prev, hours:false }));
        }

        // Fallback réseau uniquement si ABSENT du cache
        if (sidStr && !(weeklyById?.[sidStr] || weeklyHours?.[sidStr])) {
      (async () => {
        try {
          const wh = await apiGet(`/api/operators/${selectedId}/weekly-hours`, { signal: ac.signal });
          if (!alive) return;
          setWeekHours(formatWeeklyHours(wh));
        } catch {/* ignore */}
        finally {
          if (alive) setLoadingCards(prev => ({ ...prev, hours:false }));
        }
      })();
    } else if (!selectedId) {
      setWeekHours([]);
      setLoadingCards(prev => ({ ...prev, hours:false }));
    }

    return () => { alive = false; ac.abort(); };
  }, [selectedId, weeklyById, weeklyHours]);

  // (A) Fetch chart logs quand la fenêtre du graphe change
  useEffect(() => {
    const toExcl = ymd(addDays(range.to, 1));
    const key = cacheKey(range.fromYMD, toExcl);
    let alive = true;
    const ac = new AbortController();
    // si déjà en cache -> pas de réseau
    if (logsCache.has(key)) {
      setLoadingCards(p => ({ ...p, chart:false }));
      return () => {};
    }
    setLoadingCards(p => ({ ...p, chart:true }));
    (async () => {
      try {
        const logsRaw = await apiGet('/api/logs', {
          date_from: range.fromYMD,
          date_to:   toExcl,   // fin exclusive
          limit: 5000,
          signal: ac.signal
        });
        if (!alive) return;
        const all = asArray(logsRaw).filter(isTaskLike);
        const realised = all.filter(l => {
          const s = statusNormalized(l);
          return s === 'done' || s === 'incident' || s === 'failed' || s === 'missed';
        });
        const uniqAll = dedupeByInstance(realised);
        logsCache.set(key, uniqAll);
        trimMapInPlace(logsCache, 12);
        persistMap(logsCache, keyLogs(uid));
        setChartCacheTick(t => t + 1);
      } catch (e) {
        if (e?.name !== 'AbortError') console.error('[chart logs fetch]', e);
        logsCache.set(key, []); // cache vide pour éviter re-fetch en boucle
        trimMapInPlace(logsCache, 12);
        persistMap(logsCache, keyLogs(uid));
        setChartCacheTick(t => t + 1);
      } finally {
        if (alive) setLoadingCards(p => ({ ...p, chart:false }));
      }
    })();
    return () => { alive = false; ac.abort(); };
  }, [range.fromYMD, range.toYMD, period]);

  // (B) Recompute séries du graphe à partir du cache quand l'opérateur change
  useEffect(() => {
    if (!selectedId) return;
    const toExcl = ymd(addDays(range.to, 1));
    const key = cacheKey(range.fromYMD, toExcl);
    const all = logsCache.get(key);
    if (!all) return; // le fetch (A) mettra à jour bientôt
    startTransition(() => {
      const buckets = makeBuckets(period, range.from, range.to);
      const uniqOp = all.filter(l => {
        const ids = assignedIdsFor(l, operatorsSafe);
        return ids.includes(String(selectedId));
      });
      const cntAll = {}, cntOp = {};
      all.forEach(l  => { const k = bucketKeyForTask(l, period); cntAll[k] = (cntAll[k] || 0) + 1; });
      uniqOp.forEach(l=> { const k = bucketKeyForTask(l, period); cntOp[k]  = (cntOp[k]  || 0) + 1; });
      const divisor = Math.max(1, operatorsSafe.length);
      const refined = buckets.map(b => ({
        key: b.key,
        monday: b.mondayIso,
        label: b.label,
        op:   cntOp[b.key]   || 0,
        avg:  Number(((cntAll[b.key] || 0) / divisor).toFixed(2))
      }));
      setSeriesChart(refined);
    });
  }, [selectedId, period, range.fromYMD, range.toYMD, operatorsSafe, chartCacheTick]);

  // (C1) Fetch incidents logs quand la fenêtre incidents change
  useEffect(() => {
    const toExcl = ymd(addDays(incRange.to, 1));
    const key = cacheKey(incRange.fromYMD, toExcl);
    let alive = true;
    const ac = new AbortController();
    if (incCache.has(key)) {
      setLoadingCards(p => ({ ...p, pie:false, history:false }));
      return () => {};
    }
    setLoadingCards(p => ({ ...p, pie:true, history:true }));
    (async () => {
      try {
        const raw = await apiGet('/api/logs', {
          date_from: incRange.fromYMD,
          date_to:   toExcl,
          limit: 5000,
          signal: ac.signal
        });
        if (!alive) return;
        const all = asArray(raw).filter(isTaskLike);
        const realised = dedupeByInstance(
          all.filter(l => ['done','incident','failed','missed'].includes(statusNormalized(l)))
        );
        incCache.set(key, realised);
        trimMapInPlace(incCache, 12);
        persistMap(incCache, keyInc(uid));
        setIncCacheTick(t => t + 1);
      } catch (e) {
        if (e?.name !== 'AbortError') console.error('[incidents logs fetch]', e);
        incCache.set(key, []);
        trimMapInPlace(incCache, 12);
        persistMap(incCache, keyInc(uid));
        setIncCacheTick(t => t + 1);
      } finally {
        if (alive) setLoadingCards(p => ({ ...p, pie:false, history:false }));
      }
    })();
    return () => { alive = false; ac.abort(); };
  }, [incRange.fromYMD, incRange.toYMD, incPeriod]);

  // (C2) Recompute incidents/historique à partir du cache quand l'opérateur change
  useEffect(() => {
    if (!selectedId) return;
    const toExcl = ymd(addDays(incRange.to, 1));
    const key = cacheKey(incRange.fromYMD, toExcl);
    const realised = incCache.get(key);
    if (!realised) return;
    startTransition(() => {
      const byOp = new Map();   // id -> {done, incident, notDone, notFeasible}
      const hist = new Map();   // id -> [{date,title,status}]
      const ensure = (id) => { if (!byOp.has(id)) byOp.set(id, emptyPieCounters()); return byOp.get(id); };
      for (const l of realised) {
        const s = statusNormalized(l);
        const ids = assignedIdsFor(l, operatorsSafe);
        if (!ids.length) continue;
        const when  = ymd(eventDateForLog(l));
        const title = l?.meta?.task_title || l?.title || 'Tâche';
        const pretty = incidentStatusLabel(l);
        for (const idRaw of ids) {
          const id = String(idRaw);
          const c = ensure(id);
          if (s === 'done') c.done++;
          else if (s === 'incident') c.incident++;
          else if (s === 'failed') c.notFeasible++;
          else if (s === 'missed') c.notDone++;
          if (s !== 'done') {
            if (!hist.has(id)) hist.set(id, []);
            hist.get(id).push({ date: when, title, status: pretty });
          }
        }
      }
      for (const arr of hist.values()) arr.sort((a,b) => (a.date < b.date ? 1 : -1));
      const sid = String(selectedId);
      setPieStats(byOp.get(sid) || emptyPieCounters());
      setIncidentItems(hist.get(sid) || []);
    });
  }, [selectedId, incRange.fromYMD, incRange.toYMD, operatorsSafe, incCacheTick]);

  // const isAnyLoading = loading || Object.values(loadingCards).some(Boolean);

  return (
    <div className="operators-page">
      {/* Barre latérale */}
      <div className="sidebar">
        <div className="logo">CabConnect</div>
        <nav className="nav-menu">
          <div className="nav-item" onClick={() => onNavigate && onNavigate('tasks')}>
            <span className="nav-icon">📁</span>
            <span>Taches</span>
          </div>
          <div className="nav-item" onClick={() => onNavigate && onNavigate('sterilization')}>
            <span className="nav-icon">📦</span>
            <span>Stérilisation</span>
          </div>
          <div className="nav-item active">
            <span className="nav-icon">👤</span>
            <span>Opérateurs</span>
          </div>
          <div className="nav-item" onClick={() => onNavigate && onNavigate('activity')}>
            <span className="nav-icon">📊</span>
            <span>Activité</span>
          </div>
          <div className="nav-item" onClick={() => onNavigate && onNavigate('agenda')}>
            <span className="nav-icon">🗓️</span>
            <span>Agenda</span>
          </div>
          <div className="nav-item" onClick={() => onNavigate && onNavigate('orders')}>
            <span className="nav-icon">🛒</span>
            <span>Commandes</span>
          </div>
        </nav>
      </div>

      {/* Contenu principal */}
      <div className="main-content">
        {/* En-tête */}
        <div className="header">
          <div className="header-left">
            <h1>Opérateurs</h1>

            {/* Sélecteur bleu sous le titre */}
            <BlueOperatorPicker
              operators={operatorsSafe}
              value={selectedId}
              onChange={setSelectedId}
            />
          </div>
          <div className="header-right">
            <AddOperatorButton
              uid={uid}
              onCreated={(op) => {
                if (!op || !op.id) return;
                setOperators(prev => {
                  const next = Array.isArray(prev) ? [...prev] : [];
                  // évite les doublons si l'API renvoie un op déjà présent
                  const idx = next.findIndex(o => Number(o.id) === Number(op.id));
                  if (idx >= 0) next[idx] = { ...next[idx], ...op };
                  else next.unshift(op);
                  return next;
                });
                setSelectedId(op.id);
              }}
            />
          </div>
        </div>

        {error && <div className="operators-error">{error}</div>}

        <div className="operators-grid">
        {/* 1) Panneau opérateur (haut-gauche) */}
        <div className="operators-card panel-card">
          <div className="op-header">
            <div className="op-name">
              {selectedOp ? `${selectedOp.first_name} ${selectedOp.last_name}` : '—'}
            </div>
            {selectedOp && (
              <div className="op-role-chip">
                {String(selectedOp.role || selectedOp.contract_type || 'Salarié')}
              </div>
            )}
          </div>

          {/* Onglets centrés */}
          <ModeTabsCentered value={mode} onChange={setMode} />

          {/* Éditeur de la semaine */}
          {loadingCards.hours ? (
            <Skeleton n={7} />
          ) : (
            <>
              {mode === 'agenda' && (
                <WeeklyHoursEditor
                  operatorId={selectedId ? Number(selectedId) : null}
                  days={weekHours}
                  disabled={!selectedOp} // sécurité
                  onSaved={(weekFlat) => {
                    // 1) rafraîchir la carte en cours
                    setWeekHours(formatWeeklyHours(weekFlat));
                    // 2) mémoriser pour cet opérateur (évite un refetch)
                    const sid = String(selectedId);
                    setWeeklyHoursByOp(prev => ({ ...prev, [sid]: { week: weekFlat, tz: 'Europe/Paris', fetchedAt: Date.now() } }));
                    // 3) pousser dans le cache partagé (pour les autres composants)
                    const all = cacheGet(OPS_WH_KEY, {}) || {};
                    all[sid] = weekFlat;
                    cacheSet(OPS_WH_KEY, all);
                    emitCacheUpdated(OPS_WH_KEY);
                  }}
                />
              )}
              {mode === 'absence' && (
                <AbsencePlanner
                  operatorId={selectedId ? Number(selectedId) : null}
                  tz="Europe/Paris"
                />
              )}
              {mode === 'documents' && <div className="operators-empty">Documents (à venir)</div>}
            </>
          )}
        </div>

        {/* 2) Graphique (haut-droite) */}
        <div className="operators-card chart-card">
          <div className="operators-card-title with-tabs">
            <span>Nombre de tâches réalisées</span>
            <PeriodTabs value={period} onChange={setPeriod}/>
          </div>
          {loadingCards.chart ? <Skeleton n={1} h={220}/> : <TasksChart data={seriesChart} period={period}/>}
        </div>

        {/* 3) Pie incidents (colonne de droite, sous le graphique) */}
        <div className="operators-card pie-card">
          <div className="operators-card-title with-tabs">
            <span>Incidents (%)</span>
            <IncidentTabs value={incPeriod} onChange={(v)=>setIncPeriod(v)} />
          </div>
          {loadingCards.pie ? <Skeleton n={1} h={200}/> : <IncidentPie stats={pieStats}/>}
        </div>

        {/* 4) Historique incidents (sous le camembert) */}
        <div className="operators-card incidents-card">
          <div className="operators-card-title">Derniers incidents</div>
          {loadingCards.history ? (
            <Skeleton n={6}/>
          ) : (
            <>
              <IncidentHistory
                items={incidentItems.slice((incPage-1)*INC_PAGE_SIZE, incPage*INC_PAGE_SIZE)}
              />
              <Pagination
                page={incPage}
                pageSize={INC_PAGE_SIZE}
                total={incidentItems.length}
                onChange={setIncPage}
              />
            </>
          )}
        </div>

        {/* 5) Tableau tâches (colonne gauche) */}
        <div className="operators-card tasks-card">
          <div className="operators-card-title">
            Tâches {prefsLoading && <span className="sync-dot" title="Mise à jour des préférences…">•</span>}
          </div>
          {loadingCards.table ? <Skeleton n={6}/> :
            <TasksTable rows={taskRows} checked={checkedTaskIds} onToggle={toggleTaskChecked} taskPrefs={taskPrefs} />
          }
        </div>
      </div>
    </div>
  </div>
  );
}

/* ========= Small UI components (dans ce fichier, pas ailleurs) ========= */
function BlueOperatorPicker({ operators, value, onChange }) {
  return (
    <div className="blue-picker">
      <select
        value={String(value ?? '')}
        onChange={(e)=> onChange(e.target.value)}
      >
        {operators.map(o => (
          <option key={o.id} value={o.id}>
            {o.first_name} {o.last_name}
          </option>
        ))}
      </select>
      <span className="chevron">▾</span>
    </div>
  );
}

function ModeTabsCentered({ value, onChange }) {
  const Tab = ({ id, label }) => (
    <button
      className={`mode-pill ${value === id ? 'active' : ''}`}
      onClick={() => onChange(id)}
    >
      {label}
    </button>
  );
  return (
    <div className="mode-switch-centered">
      <Tab id="agenda" label="Agenda" />
      <Tab id="absence" label="Absence" />
      <Tab id="documents" label="Documents" />
    </div>
  );
}

function WeeklyHoursEditor({ operatorId, days, onSaved, disabled }) {
  const baseLabels = ['Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi','Dimanche'];
  const makeBase = () =>
    baseLabels.map(label => ({ label, active: false, start: '', end: '', endNextDay: false }));

  const [rows, setRows] = useState(() => (Array.isArray(days) && days.length ? days : makeBase()));
  const [formError, setFormError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRows(Array.isArray(days) && days.length ? days : makeBase());
    setFormError('');
    setSuccessMsg('');
  }, [days]);

  // Helpers
  const isHHMM = (s) => typeof s === 'string' && /^\d{2}:\d{2}$/.test(s);
  const timeToMinutes = (s) => { const [h,m]=s.split(':').map(Number); return h*60+m; };
  const isStartBeforeEnd = (a,b) => timeToMinutes(a) < timeToMinutes(b);

  const startRefs = useRef([]);
  const endRefs = useRef([]);

  const baseRows = useMemo(() => (Array.isArray(days) && days.length ? days : makeBase()), [days]);
  const [baseline, setBaseline] = useState(baseRows);
  useEffect(() => { setBaseline(baseRows); }, [baseRows]);
  const dirty = useMemo(() => JSON.stringify(rows) !== JSON.stringify(baseline), [rows, baseline]);
  const prevDirtyRef = useRef(dirty);
  useEffect(() => {
    const prev = prevDirtyRef.current;
    prevDirtyRef.current = dirty;
    // On efface les messages uniquement quand on COMMENCE une nouvelle modif (false -> true)
    if (!prev && dirty) { setSuccessMsg(''); setFormError(''); }
  }, [dirty]);

  const handleToggle = (i) => {
    setFormError('');
    setSuccessMsg('');
    setRows(r => r.map((d,idx) => {
      if (idx !== i) return d;
      const nextActive = !d.active;
      return {
        ...d,
        active: nextActive,
        start: nextActive ? d.start : '',
        end:   nextActive ? d.end   : '',
        endNextDay: false
      };
    }));
  };

  const handleTimeChange = (i, field, value) => {
    setRows(r => r.map((d,idx) => {
      if (idx !== i) return d;
      const next = { ...d, [field]: value };
      if (value && !d.active) next.active = true;
      return next;
    }));
    setFormError('');
  };

  const ensureActive = (i, which) => {
    if (!operatorId || disabled) return;

    setRows(r => {
      const row = r[i];
      if (!row) return r;

      const field = which === 'end' ? 'end' : 'start';
      const val = row[field];

      // active la ligne + initialise si vide à 00:00
      const next = r.map((d, idx) => {
        if (idx !== i) return d;
        return {
          ...d,
          active: true,
          [field]: val && /^\d{2}:\d{2}$/.test(val) ? val : '00:00'
        };
      });

      // focus + sélection sur les **heures** (positions 0..2)
      requestAnimationFrame(() => {
        const el = which === 'end' ? endRefs.current[i] : startRefs.current[i];
        if (!el) return;
        el.focus();
        try { el.setSelectionRange(0, 2); } catch {}
        // el.showPicker?.(); // optionnel
      });

      return next;
    });
  };

  const cancel = () => {
    setRows(baseRows);
    setFormError('');
    setSuccessMsg('');
  };

  const handleMouseDownTime = (e, i, which) => {
    // Empêche le navigateur de positionner le caret sur "minutes"
    e.preventDefault();
    ensureActive(i, which); // fera focus + caret sur HEURES
  };

  const save = async () => {
    setFormError('');
    setSuccessMsg('');
    if (!operatorId || !dirty) return;

    // Validation
    const invalid = [];
    rows.forEach((d, i) => {
      if (!d.active) return;
      if (!d.start || !d.end) invalid.push(`${baseLabels[i]}: heures manquantes`);
      else if (!isHHMM(d.start) || !isHHMM(d.end)) invalid.push(`${baseLabels[i]}: format invalide (HH:MM)`);
      else if (!isStartBeforeEnd(d.start, d.end)) invalid.push(`${baseLabels[i]}: début doit être < fin`);
    });
    if (invalid.length) {
      setFormError(`Corrige ces lignes avant de sauver:\n• ${invalid.join('\n• ')}`);
      return;
    }

    // Payload BULK (gère ajout/modif/suppression)
    // Le backend attend le format HH:MM (sans secondes)
    const toHHMM = (s) => (typeof s === 'string' ? s.slice(0, 5) : '');

    const buildWeek = () => {
      const w = {};
      for (let i = 0; i < 7; i++) {
        const r = rows[i];
        const k = String(i + 1); // 1..7
        w[k] = (r.active && r.start && r.end)
          ? { start: toHHMM(r.start), end: toHHMM(r.end), end_next_day: false }
          : null;
      }
      return w;
    };

    setSaving(true);
    try {
      const payload = {
        week: buildWeek(),
        effective_from: ymd(new Date())
      };
      
      // OK : autoriser 0 jour actif (semaine fermée). Le backend recevra week:{1:null,...,7:null}
      
      // Debug temporaire - à retirer après
      console.log('Payload envoyé:', JSON.stringify(payload, null, 2));
      
      const res = await apiPut(`/api/operators/${operatorId}/weekly-hours/bulk`, payload);
      const freshWeekFlat = res?.week || (await apiGet(`/api/operators/${operatorId}/weekly-hours`));
      onSaved?.(freshWeekFlat);
      const allNull = Object.values(payload.week).every(v => v === null);
      setSuccessMsg(allNull ? 'Semaine fermée enregistrée.' : 'Modifications enregistrées.');
      setBaseline(rows);   // dirty=false -> bouton "Sauver" se grise
    } catch (e) {
      // Fallback si le back veut *_local + tz
      if (e.status === 400) {
        try {
          const w = {};
          for (let i = 0; i < 7; i++) {
            const r = rows[i];
            const k = String(i + 1);
            w[k] = (r.active && r.start && r.end)
              ? { start_local: toHHMM(r.start), end_local: toHHMM(r.end), end_next_day: false, tz: 'Europe/Paris' }
              : null;
          }
          await apiPost(`/api/operators/${operatorId}/weekly-hours/bulk`, {
            week: w,
            effective_from: ymd(new Date())
          });
          setSuccessMsg('Modifications enregistrées.');
          setBaseline(rows);
        } catch (fallbackError) {
          const msg = String(fallbackError?.message || '');
          if (msg.includes('409') || /overlap|exclusion|chevauchement/i.test(msg)) {
            setFormError('Chevauchement d\'horaires (409). Modifie la plage ou ferme l\'ancienne période.');
          } else if (msg.includes('400')) {
            setFormError('Requête invalide (400). Vérifie les heures au format HH:MM.');
          } else if (msg.includes('401')) {
            setFormError('Session expirée (401). Reconnecte-toi.');
          } else if (msg.includes('403')) {
            setFormError('Accès interdit à cet opérateur (403).');
          } else {
            setFormError('Erreur serveur (500). Réessaie.');
          }
        }
      } else {
        const msg = String(e?.message || '');
        if (msg.includes('409') || /overlap|exclusion|chevauchement/i.test(msg)) {
          setFormError('Chevauchement d\'horaires (409). Modifie la plage ou ferme l\'ancienne période.');
        } else if (msg.includes('400')) {
          setFormError('Requête invalide (400). Vérifie les heures au format HH:MM.');
        } else if (msg.includes('401')) {
          setFormError('Session expirée (401). Reconnecte-toi.');
        } else if (msg.includes('403')) {
          setFormError('Accès interdit à cet opérateur (403).');
        } else {
          setFormError('Erreur serveur (500). Réessaie.');
        }
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="op-week">
      {rows.map((d, i) => {
        const invalidStart = d.active && !d.start;
        const invalidEnd   = d.active && !d.end;
        return (
          <div key={i} className="op-week-row">
            <label className="check-and-day">
              <input
                type="checkbox"
                className="op-check"
                checked={!!d.active}
                onChange={() => handleToggle(i)}
                disabled={!operatorId || disabled}
                aria-label={`Activer ${d.label}`}
              />
              <span className="op-day">{d.label}</span>
            </label>

            <div className="op-times">
              <div className="time-input-wrap">
                <input
                  ref={el => (startRefs.current[i] = el)}
                  className={`time-input ${invalidStart ? 'invalid' : ''}`}
                  type="time"
                  step="60"
                  value={d.start || ''}
                  onChange={(e) => handleTimeChange(i, 'start', e.target.value)}
                  onMouseDown={(e) => handleMouseDownTime(e, i, 'start')}
                  onFocus={() => ensureActive(i, 'start')}
                  disabled={!operatorId || disabled}
                />
              </div>
              <span className="arrow">→</span>
              <div className="time-input-wrap">
                <input
                  ref={el => (endRefs.current[i] = el)}
                  className={`time-input ${invalidEnd ? 'invalid' : ''}`}
                  type="time"
                  step="60"
                  value={d.end || ''}
                  onChange={(e) => handleTimeChange(i, 'end', e.target.value)}
                  onMouseDown={(e) => handleMouseDownTime(e, i, 'end')}
                  onFocus={() => ensureActive(i, 'end')}
                  disabled={!operatorId || disabled}
                />
              </div>
            </div>
          </div>
        );
      })}

      {/* Messages */}
      {successMsg && !formError && (
        <div className="form-success" role="status" aria-live="polite">✅ {successMsg}</div>
      )}
      {formError && <div className="form-error">{formError}</div>}

      <div className="row-actions">
        <button
          className="btn btn-ghost"
          onClick={() => setRows(r => r.map(d => ({...d, active:false, start:'', end:'', endNextDay:false})))}
        >
          Tout désactiver
        </button>
        {!operatorId && (
          <div className="operators-hint">Sélectionnez un opérateur pour modifier les horaires</div>
        )}
        <button className="btn btn-ghost" onClick={cancel}>Annuler</button>
        <button className="btn btn-primary" onClick={save} disabled={!operatorId || saving || !dirty}>
          {saving ? 'Sauvegarde…' : 'Sauver'}
        </button>
      </div>
    </div>
  );
}

function PeriodTabs({ value, onChange }) {
  return (
    <div className="operators-tabs">
      <button className={value==='day' ? 'active' : ''} onClick={()=>onChange('day')}>Jour</button>
      <button className={value==='week' ? 'active' : ''} onClick={()=>onChange('week')}>Semaine</button>
      <button className={value==='month' ? 'active' : ''} onClick={()=>onChange('month')}>Mois</button>
    </div>
  );
}

function IncidentTabs({ value, onChange }) {
  return (
    <div className="operators-tabs">
      <button className={value==='week' ? 'active' : ''}  onClick={()=>onChange('week')}>Semaine</button>
      <button className={value==='month' ? 'active' : ''} onClick={()=>onChange('month')}>Mois</button>
      <button className={value==='6m' ? 'active' : ''}    onClick={()=>onChange('6m')}>6&nbsp;mois</button>
    </div>
  );
}

function Pagination({ page, pageSize, total, onChange }) {
  const pages = Math.max(1, Math.ceil((total||0) / pageSize));
  if (pages <= 1) return null;
  const arr = Array.from({length: pages}, (_,i)=>i+1);
  return (
    <div className="pagination">
      {arr.map(p => (
        <button key={p} className={`page-btn ${p===page ? 'active' : ''}`} onClick={()=>onChange(p)}>
          {p}
        </button>
      ))}
    </div>
  );
}

const NiceTooltip = ({ active, payload, label, period }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0]?.payload || {};

  let title = label;
  if (period === 'day'  && p.key)              title = dayTooltipText(p.key);
  if (period === 'week' && (p.monday||p.key))  title = `Semaine du ${ddmm(p.monday || p.key)}`;

  const valOp  = payload.find(x => x.dataKey === 'op')?.value ?? 0;
  const valAvg = payload.find(x => x.dataKey === 'avg')?.value ?? 0;

  return (
    <div style={{background:'#0b0f16',border:'1px solid #223046',borderRadius:10,padding:'10px 12px',color:'#e5e7eb'}}>
      <div style={{fontWeight:700, marginBottom:6}}>{title}</div>
      <div style={{display:'grid', gap:4}}>
        {/* ⇣ Opérateur en premier ⇣ */}
        <div style={{display:'flex', justifyContent:'space-between', gap:12}}>
          <span style={{opacity:.85}}>Opérateur</span>
          <span style={{fontVariantNumeric:'tabular-nums'}}>{valOp}</span>
        </div>
        <div style={{display:'flex', justifyContent:'space-between', gap:12}}>
          <span style={{opacity:.85}}>Moyenne</span>
          <span style={{fontVariantNumeric:'tabular-nums'}}>{valAvg}</span>
        </div>
      </div>
    </div>
  );
};

const TasksChart = React.memo(function TasksChart({ data, period }) {
  if (!Array.isArray(data) || data.length === 0) {
    return <div className="operators-empty">Aucune donnée</div>;
  }
  return (
    <div className="operators-chart-wrap">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
          {/* plus de pointillés */}
          <XAxis dataKey="label" />
          <YAxis allowDecimals={false} />
          <Tooltip content={<NiceTooltip period={period} />} />
          <Legend />
          <Line type="monotone" dataKey="op"  name="Opérateur" stroke="#318CFF" strokeWidth={2} dot={false}/>
          <Line type="monotone" dataKey="avg" name="Moyenne"   stroke="#22C55E" strokeWidth={2} dot={false}/>
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
});



const IncidentPie = React.memo(function IncidentPie({ stats }) {
  const incidentVal = (stats?.incident ?? stats?.late) | 0;

  const all = [
    { name: 'Réalisée',       value: stats?.done|0,        color: '#318CFF' },
    { name: 'Incident',       value: incidentVal,          color: '#F59E0B' },
    { name: 'Non réalisée',   value: stats?.notDone|0,     color: '#EF4444' },
    { name: 'Non réalisable', value: stats?.notFeasible|0, color: '#6B7280' },
  ];

  // ⬇️ NE GARDE QUE LES CATÉGORIES > 0
  const data = all.filter(d => (d.value|0) > 0);
  const total = data.reduce((s, d) => s + (d.value|0), 0);

  if (total === 0) return <div className="operators-empty">Aucune donnée sur la période</div>;

  return (
    <div className="operators-pie-wrap">
      <ResponsiveContainer width="100%" height={220}>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            outerRadius={80}
            label={(e)=> `${e.name} ${Math.round(100*e.value/total)}%`}
            /* ⬇️ plus de bordure blanche ni séparation */
            stroke="none"
            strokeWidth={0}
          >
            {data.map((entry, i) => (
              <Cell key={i} fill={entry.color} stroke="none" />
            ))}
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>

      {/* Légende sans items à 0 */}
      <div className="operators-legend">
        {data.map((d,i)=>(
          <div key={i} className="legend-row">
            <span className="legend-dot" style={{ background: d.color }} />
            <span>{d.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
});

const IncidentHistory = React.memo(function IncidentHistory({ items }) {
  if (!Array.isArray(items) || items.length === 0) {
    return <div className="operators-empty">Aucun incident</div>;
  }
  return (
    <div className="operators-history">
      {items.map((it, idx)=>(
        <div key={idx} className="operators-history-row">
          <div className="date">{formatFR(it.date)}</div>
          <div className="title">{it.title}</div>
          <div className={`chip chip-${chipClass(it.status)}`}>{chipIcon(it.status)} {it.status}</div>
        </div>
      ))}
    </div>
  );
});

const TasksTable = React.memo(function TasksTable({ rows, checked, onToggle, taskPrefs }) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return <div className="operators-empty">Aucune tâche</div>;
  }
  return (
    <div className="operators-table">
      <div className="tbody">
        {rows.map((r) => {
          const mode = taskPrefs?.get(String(r.__raw?.id ?? r.__raw?.task_id ?? r.id));
          return (
            <label key={r.id} className="tr task-row">
              <input
                type="checkbox"
                className="row-check"
                checked={!!(checked && typeof checked.has === 'function' && checked.has(r.id))}
                onChange={() => onToggle?.(r.id)}
              />
              <span className="td title one-line">
                {r.title}
                {mode && <span className={`pref-chip ${mode}`}>{mode === 'include' ? 'Forcée +' : 'Exclue -'}</span>}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
});

function Skeleton({ n=3, h=16 }) {
  return (
    <div className="operators-skeleton">
      {Array.from({length:n}).map((_,i)=>(
        <div key={i} className="bar" style={{ height: h }} />
      ))}
    </div>
  );
}

function AbsencePlanner({ operatorId, tz = 'Europe/Paris' }) {
  const [from] = useState(() => at00(new Date()));
  const [to]   = useState(() => addDays(at00(new Date()), 29)); // 30 jours

  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [okMsg, setOkMsg]     = useState('');

  // sélection (2 clics = intervalle)
  const [anchor, setAnchor] = useState(null);
  const [cursor, setCursor] = useState(null);

  // type + heures (pour "Ouverture")
  const [kind, setKind]   = useState('off');     // 'off' = Congé | 'open' = Ouverture
  const [start, setStart] = useState('09:00');
  const [end,   setEnd]   = useState('19:00');

  // --- helpers temps
  const isHHMM = (s) => /^\d{2}:\d{2}$/.test(String(s||''));
  const mins = (s) => { const [h,m]=String(s).split(':').map(Number); return h*60+m; };
  const hoursFirstMouseDown = (e) => {
    e.preventDefault();
    const el = e.currentTarget;
    if (!el.value) el.value = '00:00';
    requestAnimationFrame(() => { el.focus(); try { el.setSelectionRange(0,2); } catch {} });
  };

  // --- range 30 jours + "break" de mois (sub-headers)
  const days = useMemo(() => {
    const arr = [];
    for (let d = new Date(from); d <= to; d = addDays(d, 1)) {
      const iso = ymd(d);
      const m   = d.toLocaleString('fr-FR', { month:'long' });
      const dow = d.toLocaleString('fr-FR', { weekday:'short' }).replace('.','');
      arr.push({ iso, label: String(d.getDate()).padStart(2,'0'), dow, month: m });
    }
    return arr;
  }, [from, to]);

  // sélection calculée
  const inRange = (iso) => {
    if (!anchor) return false;
    const a = anchor;
    const b = cursor && cursor >= a ? cursor : a;
    return iso >= a && iso <= b;
  };
  const isStart = (iso) => !!anchor && !!cursor && iso === anchor && anchor !== cursor;
  const isEnd   = (iso) => !!anchor && !!cursor && iso === cursor  && anchor !== cursor;

  // load overrides
  useEffect(() => {
    if (!operatorId) return;
    let alive = true;
    setLoading(true); setError(''); setOkMsg('');
    (async () => {
      try {
        const q = `?from=${ymd(from)}&to=${ymd(to)}`;
        const rows = await apiGet(`/api/operators/${operatorId}/overrides${q}`);
        const map = {}; (rows || []).forEach(r => { map[String(r.date)] = r; });
        if (alive) setOverrides(map);
      } catch { if (alive) setError('Impossible de charger les absences.'); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [operatorId, from, to]);

  // interactions
  const clickDay = (iso) => {
    setOkMsg(''); setError('');
    if (!anchor || (anchor && cursor)) { setAnchor(iso); setCursor(null); return; }
    if (iso < anchor) { setCursor(anchor); setAnchor(iso); return; }
    setCursor(iso);
  };
  const clearSelection = () => { setAnchor(null); setCursor(null); };

  const selectionList = useMemo(() => {
    if (!anchor) return [];
    const a = anchor;
    const b = cursor && cursor >= anchor ? cursor : anchor;
    const out = [];
    for (let d = new Date(a); ymd(d) <= b; d = addDays(d, 1)) out.push(ymd(d));
    return out;
  }, [anchor, cursor]);

  const anySelectedHasOverride = useMemo(
    () => selectionList.some(d => !!overrides[d]),
    [selectionList, overrides]
  );

  // actions
  const save = async () => {
    setError(''); setOkMsg('');
    if (!operatorId) return;
    if (!selectionList.length) { setError('Sélectionne un jour ou une période.'); return; }

    if (kind === 'open') {
      if (!isHHMM(start) || !isHHMM(end)) { setError('Heures invalides (HH:MM).'); return; }
      if (mins(start) >= mins(end)) { setError('Début doit être < fin.'); return; }
    }

    setSaving(true);
    try {
      const payload = kind === 'off'
        ? { is_off: true,  start_local: null, end_local: null, end_next_day: false, tz }
        : { is_off: false, start_local: start, end_local: end, end_next_day: false, tz };

      const ops = selectionList.map(d =>
        apiPut(`/api/operators/${operatorId}/overrides/${d}`, payload).then(r => ({ d, r }))
      );
      const res = await Promise.allSettled(ops);
      const next = { ...overrides };
      res.forEach(x => { if (x.status === 'fulfilled') next[x.value.d] = x.value.r.override; });
      setOverrides(next);
      setOkMsg('Enregistré.');
    } catch { setError('Erreur lors de l\'enregistrement.'); }
    finally { setSaving(false); }
  };

  const removeSelected = async () => {
    setError(''); setOkMsg('');
    const dates = selectionList.filter(d => overrides[d]);
    if (!dates.length) { setError('Aucune exception à supprimer.'); return; }
    setSaving(true);
    try {
      const ops = dates.map(d => apiDelete(`/api/operators/${operatorId}/overrides/${d}`).then(()=>d));
      const done = await Promise.allSettled(ops);
      const next = { ...overrides };
      done.forEach(x => { if (x.status === 'fulfilled') delete next[x.value]; });
      setOverrides(next);
      setOkMsg('Supprimé.');
    } catch { setError('Suppression impossible.'); }
    finally { setSaving(false); }
  };

  // rendu
  let lastMonth = '';
  return (
    <div className="abs2 compact">
      <div className="abs2-head">
        <div className="title">Absences & ouvertures</div>
        <div className="hint">30 jours</div>
      </div>

      <div className="abs2-controls">
        <div className="segmented">
          <button className={kind==='off' ? 'active' : ''} onClick={()=>setKind('off')}>Congé</button>
          <button className={kind==='open' ? 'active' : ''} onClick={()=>setKind('open')}>Ouverture</button>
        </div>

        {kind === 'open' && (
          <div className="hours">
            <input
              type="time"
              value={start}
              onChange={e=>setStart(e.target.value)}
              onMouseDown={hoursFirstMouseDown}
              onFocus={hoursFirstMouseDown}
              className="cupertino"
            />
            <span className="arrow">→</span>
            <input
              type="time"
              value={end}
              onChange={e=>setEnd(e.target.value)}
              onMouseDown={hoursFirstMouseDown}
              onFocus={hoursFirstMouseDown}
              className="cupertino"
            />
          </div>
        )}
      </div>

      <div className="abs2-grid">
        {loading ? (
          <Skeleton n={8} />
        ) : (
          days.map(({ iso, label, dow, month }) => {
            const ov = overrides[iso];
            const sel = inRange(iso);
            const startFlag = isStart(iso);
            const endFlag   = isEnd(iso);

            const monthBreak = month !== lastMonth;
            if (monthBreak) lastMonth = month;

            return (
              <React.Fragment key={iso}>
                {monthBreak && <div className="month-break">{month.toUpperCase()}</div>}
                <button
                  className={
                    'day' +
                    (ov ? (ov.is_off ? ' is-off' : ' is-open') : '') +
                    (sel ? ' is-selected' : '') +
                    (startFlag ? ' is-start' : '') +
                    (endFlag   ? ' is-end'   : '')
                  }
                  onClick={()=>clickDay(iso)}
                >
                  <div className="dow">{dow}</div>
                  <div className="num">{label}</div>
                  {ov ? (
                    ov.is_off
                      ? <div className="badge badge-off">Congé</div>
                      : <div className="badge badge-open">{String(ov.start_local||'').slice(0,5)}–{String(ov.end_local||'').slice(0,5)}</div>
                  ) : <div className="dash">—</div>}
                </button>
              </React.Fragment>
            );
          })
        )}
      </div>

      {(error || okMsg) && <div className={`abs2-msg ${error ? 'err' : 'ok'}`}>{error || okMsg}</div>}

      <div className="abs2-actions">
        <button className="btn-ghost" onClick={() => { clearSelection(); setKind('off'); setStart('09:00'); setEnd('19:00'); }}>
          Annuler
        </button>
        <div className="spacer" />
        <button className="btn-danger" onClick={removeSelected} disabled={!anySelectedHasOverride || saving}>Supprimer</button>
        <button className="btn-primary" onClick={save} disabled={!operatorId || saving || selectionList.length===0}>
          {saving ? 'Sauvegarde…' : 'Sauver'}
        </button>
      </div>

      <div className="abs2-legend">
        <span className="dot off"/> Congé
        <span className="dot open"/> Ouverture
        <span className="dot sel"/> Sélection
      </div>
    </div>
  );
}

/* ========= Local helpers (format/mapping) ========= */
function countByBucket(logs, period) {
  const cnt = {};
  (Array.isArray(logs) ? logs : []).forEach(l => {
    const created = new Date(l.created_at || l.createdAt || Date.now());
    let key;
    if (period === 'day') key = dayKey(created);
    else if (period === 'month') key = monthKey(created);
    else key = weekKey(created);
    cnt[key] = (cnt[key] || 0) + 1;
  });
  return cnt;
}
function extractIncidentStats(logs) {
  const arr = Array.isArray(logs) ? logs : [];
  return {
    done: arr.filter(isDone).length,
    late: arr.filter(isLate).length,
    notDone: arr.filter(isNotDone).length,
    notFeasible: arr.filter(isNotFeasible).length,
  };
}
function incidentStatusLabel(log) {
  const s = statusNormalized(log);
  if (s === 'done')     return 'Réalisée';
  if (s === 'incident') return 'Incident';
  if (s === 'missed')   return 'Non réalisée';
  if (s === 'failed')   return 'Non réalisable';
  return 'Incident';
}
function chipClass(status) {
  const s = String(status).toLowerCase();
  if (s.includes('incident')) return 'orange';
  if (s.includes('non réalisée')) return 'red';
  if (s.includes('non réalisable')) return 'gray';
  return 'orange';
}
function chipIcon(status) {
  const s = String(status).toLowerCase();
  if (s.includes('non réalisable')) return '⛔'; // on garde pour "non réalisable"
  if (s.includes('non réalisée')) return '';     // pas d'icône pour "non réalisée"
  if (s.includes('retard')) return '⚠';
  return '⚠';
}
function formatFR(ymdStr) {
  if (!ymdStr) return '';
  const [Y,M,D] = ymdStr.split('-').map(Number);
  return `${String(D).padStart(2,'0')}/${String(M).padStart(2,'0')}/${Y}`;
}
function formatWeeklyHours(wh) {
  const labels = ['Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi','Dimanche'];

  // 1) Si le back renvoie le "flat" (objet {1:{start,...}, 2:null,...})
  if (wh && typeof wh === 'object' && !Array.isArray(wh)) {
    return labels.map((label, idx) => {
      const k = String(idx + 1);
      const d = wh[k];
      if (!d) return { label, active: false, start: '', end: '', endNextDay: false };
      const start = (d.start || d.start_local || '').slice(0,5);
      const end   = (d.end   || d.end_local   || '').slice(0,5);
      const endNextDay = !!(d.end_next_day);
      if (!start || !end) return { label, active: false, start: '', end: '', endNextDay: false };
      return { label, active: true, start, end, endNextDay };
    });
  }

  // 2) Fallback: tableau de lignes {weekday,start_local,end_local,...}
  const byDay = new Map();
  (Array.isArray(wh) ? wh : []).forEach(r => {
    const wd = Number(r.weekday);
    if (wd >= 1 && wd <= 7) {
      byDay.set(wd, {
        start: (r.start_local || '').slice(0,5),
        end:   (r.end_local   || '').slice(0,5),
        endNextDay: !!r.end_next_day
      });
    }
  });

  return labels.map((label, idx) => {
    const wd = idx + 1;
    const row = byDay.get(wd);
    if (!row) return { label, active: false, start: '', end: '', endNextDay: false };
    return { label, active: true, start: row.start, end: row.end, endNextDay: row.endNextDay };
  });
}

/* ========= Add Operator Components ========= */
function AddOperatorButton({ uid, onCreated }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="btn btn-primary btn-add-op" onClick={() => setOpen(true)}>
        <span className="plus">＋</span> Ajouter un opérateur
      </button>
      {open && (
        <AddOperatorModal
          uid={uid}
          onClose={() => setOpen(false)}
          onCreated={(op) => { onCreated?.(op); setOpen(false); }}
        />
      )}
    </>
  );
}

function AddOperatorModal({ uid, onClose, onCreated }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isAssistant, setIsAssistant] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e?.preventDefault();
    setError('');
    const fn = firstName.trim();
    const ln = lastName.trim();
    if (!fn || !ln) { setError('Prénom et nom sont requis.'); return; }

    setSaving(true);
    try {
      const payload = {
        first_name: fn,
        last_name: ln,
        role: isAssistant ? 'assistant' : 'autre',   // ⬅️ conforme à la BDD
        status: 'active',
        user_id: uid || undefined
      };
      const res = await apiPost('/api/operators', payload);

      let op = res?.operator || res?.op;
      if (!op && res?.operator_id) {
        // On essaye de recharger la fiche à partir de la liste
        try {
          const list = await apiGet('/api/operators', uid ? { user_id: uid } : {});
          op = (Array.isArray(list) ? list : []).find(o => Number(o.id) === Number(res.operator_id));
        } catch {}
        // Fallback optimiste si pas trouvé
        if (!op) op = { id: res.operator_id, ...payload };
      }

      onCreated?.(op);
    } catch (err) {
      const msg = String(err?.message || '');
      if (msg.includes('409')) setError('Un opérateur identique existe déjà (409).');
      else if (msg.includes('400')) setError('Requête invalide (400). Vérifie les champs.');
      else if (msg.includes('401')) setError('Session expirée (401). Reconnecte-toi.');
      else setError('Création impossible. Réessaie.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-card" onClick={(e)=>e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="modal-head">
          <div className="title">Ajouter un opérateur</div>
          <button className="modal-close" onClick={onClose} aria-label="Fermer">✕</button>
        </div>

        <form className="modal-body" onSubmit={submit}>
          <div className="name-fields">
            <label className="field">
              <span>Prénom</span>
              <input
                className="input"
                value={firstName}
                onChange={e=>setFirstName(e.target.value)}
                placeholder="Prénom"
                autoFocus
              />
            </label>
            <label className="field">
              <span>Nom</span>
              <input
                className="input"
                value={lastName}
                onChange={e=>setLastName(e.target.value)}
                placeholder="Nom"
              />
            </label>
          </div>
          <div className="field" style={{flexDirection: 'row', alignItems: 'center', justifyContent: 'center'}}>
            <span>Assistante dentaire</span>
            <div className="switch-container">
              <button
                type="button"
                className={`switch-option ${isAssistant ? 'active' : ''}`}
                onClick={() => setIsAssistant(true)}
              >
                Oui
              </button>
              <button
                type="button"
                className={`switch-option ${!isAssistant ? 'active' : ''}`}
                onClick={() => setIsAssistant(false)}
              >
                Non
              </button>
            </div>
          </div>

          {error && <div className="form-error" style={{marginTop:8}}>{error}</div>}

          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Création…' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}